"use client";

import { motion } from "framer-motion";
import { CalendarDays, GraduationCap, Languages, Stethoscope } from "lucide-react";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import { semesterLabel, type Subject, type University } from "@/lib/universities";

const SUBJECT_INFO: Record<Subject, { duration: string; description: string }> = {
  Humanmedizin: {
    duration: "12 Semester + 3 Monate",
    description:
      "Vom vorklinischen Abschnitt über Klinik und Praktisches Jahr bis zum Staatsexamen – die Ausbildung zur Ärztin bzw. zum Arzt.",
  },
  Zahnmedizin: {
    duration: "10 Semester",
    description:
      "Theorie und präklinische Kurse, danach die klinische Behandlungsphase am Patienten – Abschluss mit dem zahnärztlichen Staatsexamen.",
  },
};

export default function CourseSection({ university }: { university: University }) {
  return (
    <motion.section
      variants={stagger(0.09)}
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      aria-labelledby="courses-heading"
    >
      <motion.p
        variants={fadeUp}
        className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-widest text-accent mb-3"
      >
        <GraduationCap size={14} aria-hidden="true" />
        Studiengänge
      </motion.p>
      <motion.h2
        variants={fadeUp}
        id="courses-heading"
        className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-ink mb-6"
      >
        Studiengänge &amp; Abschlüsse
      </motion.h2>

      <div className="grid sm:grid-cols-2 gap-4">
        {university.subjects.map((subject) => (
          <motion.div
            key={subject}
            variants={fadeUp}
            className="card-flat flex flex-col gap-3 bg-white/60 p-6"
          >
            <span className="w-11 h-11 rounded-xl bg-accent-soft text-accent flex items-center justify-center">
              <Stethoscope size={19} aria-hidden="true" />
            </span>
            <h3 className="font-display font-bold text-lg text-ink">{subject}</h3>
            <p className="text-sm text-ink-soft leading-relaxed">
              {SUBJECT_INFO[subject].description}
            </p>
            <div className="mt-auto flex flex-wrap gap-x-4 gap-y-1.5 pt-1">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-faint">
                <GraduationCap size={13} className="text-accent" aria-hidden="true" />
                {SUBJECT_INFO[subject].duration}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-faint">
                <CalendarDays size={13} className="text-accent" aria-hidden="true" />
                {semesterLabel(university.semesters)}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-faint">
                <Languages size={13} className="text-accent" aria-hidden="true" />
                {university.language}
              </span>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.section>
  );
}
